<template>
  <section id="downloads" class="section section-observed downloads-section">
    <div v-if="showSectionHeading" class="section-heading">
      <div>
        <h2>{{ meta.pageTitle }}</h2>
      </div>
    </div>

    <div class="download-tabs" role="tablist" aria-label="公开文件分类">
      <button
        v-for="category in front.publicFileCategories"
        :key="category.value"
        type="button"
        :class="{ active: front.activePublicFileCategory === category.value }"
        @click="front.selectPublicFileCategory(category.value)"
      >
        {{ category.label }}
      </button>
    </div>

    <div v-if="front.publicFilesTotal > 0" class="download-total">共 {{ front.publicFilesTotal }} 条</div>

    <div class="download-grid">
      <article
        v-for="file in front.publicFiles"
        :key="file.fileId || file.fileName"
        class="download-card"
        :class="{ active: isSelected(file) }"
      >
        <button type="button" class="download-card__main" @click="onCardClick(file)">
          <span class="download-card__tag">{{ front.publicFileCategoryLabel(file.fileCategory) }}</span>
          <strong>{{ file.fileName }}</strong>
          <small>{{ file.originalFileName || file.fileExtension || '公开文件' }}</small>
          <em>{{ isDocument(file) ? '点击预览' : '点击查看' }}</em>
        </button>
        <button
          type="button"
          class="download-card__action"
          :disabled="downloadingKey === fileKey(file)"
          @click="downloadFile(file)"
        >
          {{ downloadingKey === fileKey(file) ? '下载中...' : '下载' }}
        </button>
      </article>
      <p v-if="!front.publicFiles.length" class="muted-copy download-empty">暂无公开下载文件</p>
    </div>

    <FrontPagination
      :page-num="front.publicFilesPageNum"
      :page-size="front.publicFilesPageSize"
      :total="front.publicFilesTotal"
      @change="onPageChange"
    />

    <div
      v-if="selectedFile"
      class="download-detail"
      role="dialog"
      aria-modal="true"
      aria-label="文件预览"
      @click.self="closeFileDetail"
    >
      <div class="download-detail__panel" :class="{ 'is-wide': isDocumentPreview }">
        <header class="download-detail__head">
          <div>
            <span class="download-card__tag">{{ front.publicFileCategoryLabel(selectedFile.fileCategory) }}</span>
            <h3>{{ selectedFile.fileName }}</h3>
            <p>{{ selectedFile.originalFileName || selectedFile.fileExtension || '公开文件' }}</p>
          </div>
          <button type="button" class="ghost-action compact" @click="closeFileDetail">关闭</button>
        </header>

        <div class="download-detail__preview" :class="{ 'is-frame': isDocumentPreview && canEmbedPreview && !showPreviewFallback && !previewChecking }">
          <div v-if="previewChecking" class="download-detail__doc" role="status">
            <strong>正在加载预览…</strong>
            <p>请稍候</p>
          </div>
          <div v-else-if="showPreviewFallback" class="download-detail__doc" role="status">
            <strong>{{ previewFallbackTitle }}</strong>
            <p>{{ previewFallbackHint }}</p>
          </div>
          <img
            v-else-if="previewKind === 'image'"
            :src="previewUrl"
            :alt="selectedFile.fileName"
            @error="onPreviewMediaError"
          >
          <video
            v-else-if="previewKind === 'video'"
            :src="previewUrl"
            controls
            preload="metadata"
            @error="onPreviewMediaError"
          />
          <audio
            v-else-if="previewKind === 'audio'"
            :src="previewUrl"
            controls
            preload="metadata"
            @error="onPreviewMediaError"
          />
          <iframe
            v-else-if="canEmbedPreview"
            :src="embedPreviewUrl"
            class="download-detail__iframe"
            :title="selectedFile.fileName"
            frameborder="0"
          />
        </div>

        <footer class="download-detail__actions">
          <button
            type="button"
            class="primary-action compact"
            :disabled="downloadingKey === fileKey(selectedFile)"
            @click="downloadFile(selectedFile)"
          >
            {{ downloadingKey === fileKey(selectedFile) ? '正在下载...' : '下载文件' }}
          </button>
          <a
            v-if="canEmbedPreview && officePreviewUrl"
            class="ghost-action compact"
            :href="officePreviewUrl.replace('/embed.aspx', '/view.aspx')"
            target="_blank"
            rel="noopener"
          >
            新窗口打开预览
          </a>
        </footer>
      </div>
    </div>

    <transition name="download-toast">
      <div
        v-if="downloadError"
        class="download-error-toast"
        role="alert"
        aria-live="assertive"
      >
        <div class="download-error">
          <span class="download-error__icon" aria-hidden="true"></span>
          <span class="download-error__text">{{ downloadError }}</span>
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
import FrontPagination from '../common/FrontPagination.vue'
import { resolveDownloadErrorMessage } from '../../utils/downloadErrorMessage'

export default {
  name: 'DownloadsSection',
  components: { FrontPagination },
  inject: ['front'],
  data() {
    return {
      selectedFile: null,
      downloadingKey: '',
      downloadError: '',
      previewLoadFailed: false,
      previewChecking: false,
      downloadErrorTimer: null
    }
  },
  computed: {
    meta() {
      const page = this.front.page === 'downloads-center' ? 'downloads-center' : 'public-downloads'
      return this.front.pageMeta(page)
    },
    showSectionHeading() {
      const title = String((this.meta && this.meta.pageTitle) || '').trim()
      if (!title) return false
      const pageTitle = String(this.front.pageTitle || '').trim()
      return title !== pageTitle && title !== '资料下载' && title !== '公开文件'
        && title !== '公开文件按文档、音频、视频、照片分类维护'
    },
    previewKind() {
      const category = this.front.normalizePublicFileCategory(
        this.selectedFile && this.selectedFile.fileCategory
      )
      if (category === 'IMAGE') return 'image'
      if (category === 'VIDEO') return 'video'
      if (category === 'AUDIO') return 'audio'
      return 'document'
    },
    isDocumentPreview() {
      return this.previewKind === 'document'
    },
    previewUrl() {
      if (!this.selectedFile) return ''
      return this.front.publicFileAccessUrl(this.selectedFile)
    },
    officePreviewUrl() {
      return this.front.officeOnlinePreviewUrl(this.selectedFile)
    },
    embedPreviewUrl() {
      if (!this.selectedFile) return ''
      if (this.front.isPdfPublicFile(this.selectedFile)) {
        // 有 fileId：走本站 /content 代理流，避免 302 到外链后被 CSP frame-ancestors 拦截
        if (this.selectedFile.fileId) {
          return this.previewUrl
        }
        // CMS 等无 fileId、仅外链的 PDF：用 Google 文档预览（服务端拉取，绕过浏览器嵌入限制）
        const abs = this.front.absolutePublicFileUrl(this.selectedFile)
        if (abs && /^https:\/\//i.test(abs)) {
          try {
            const host = new URL(abs).hostname
            if (host !== 'localhost' && host !== '127.0.0.1') {
              return `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(abs)}`
            }
          } catch (e) {
            // fall through
          }
        }
        return this.previewUrl
      }
      return this.officePreviewUrl
    },
    canEmbedPreview() {
      return Boolean(this.embedPreviewUrl)
    },
    /** 媒体加载失败 / 无地址，或文档无法嵌入时，展示友好兜底 */
    showPreviewFallback() {
      if (!this.selectedFile) return false
      if (this.previewLoadFailed) return true
      if (this.previewKind === 'document') return !this.canEmbedPreview
      return !this.previewUrl
    },
    previewFallbackTitle() {
      if (this.previewKind === 'image') return '暂无法预览该照片'
      if (this.previewKind === 'video') return '暂无法预览该视频'
      if (this.previewKind === 'audio') return '暂无法预览该音频'
      if (this.front.isOfficePublicFile(this.selectedFile)) {
        return '暂无法在线预览该 Office 文档'
      }
      return '暂无法在线预览该文件'
    },
    previewFallbackHint() {
      if (this.previewKind !== 'document') {
        return '文件可能已失效、无法访问或格式不受支持，请改用下方按钮下载查看。'
      }
      if (this.previewLoadFailed) {
        return '文件可能已丢失、失效或暂不可访问，请改用下方按钮下载；若下载也失败，请联系管理员。'
      }
      if (this.front.isOfficePublicFile(this.selectedFile)) {
        return '第三方预览需要可公网访问的文件地址。当前文件可能仅内网可访问，请改用下载查看。'
      }
      return '请下载后使用本地软件打开查看。'
    }
  },
  watch: {
    'front.activePublicFileCategory'() {
      this.closeFileDetail()
    },
    'front.publicFiles'() {
      if (!this.selectedFile) return
      const stillThere = (this.front.publicFiles || []).some(item => this.sameFile(item, this.selectedFile))
      if (!stillThere) this.closeFileDetail()
    },
    previewUrl() {
      this.previewLoadFailed = false
    },
    embedPreviewUrl: {
      immediate: false,
      handler() {
        this.schedulePreviewProbe()
      }
    }
  },
  mounted() {
    this._onPreviewPublicFile = (file) => {
      this.openFileDetail(file)
    }
    this.$root.$on('paleo-preview-public-file', this._onPreviewPublicFile)
  },
  beforeDestroy() {
    this.clearDownloadErrorTimer()
    if (this._onPreviewPublicFile) {
      this.$root.$off('paleo-preview-public-file', this._onPreviewPublicFile)
    }
  },
  methods: {
    onPageChange(page) {
      this.front.changePublicFilesPage(page)
    },
    clearDownloadErrorTimer() {
      if (this.downloadErrorTimer) {
        clearTimeout(this.downloadErrorTimer)
        this.downloadErrorTimer = null
      }
    },
    clearDownloadError() {
      this.clearDownloadErrorTimer()
      this.downloadError = ''
    },
    showDownloadError(message) {
      const text = String(message || '').trim()
      this.clearDownloadErrorTimer()
      this.downloadError = text
      if (!text) return
      this.downloadErrorTimer = setTimeout(() => {
        this.downloadError = ''
        this.downloadErrorTimer = null
      }, 3200)
    },
    fileKey(file) {
      if (!file) return ''
      return file.fileId ? String(file.fileId) : `${file.fileName || ''}|${file.fileUrl || ''}`
    },
    sameFile(a, b) {
      if (!a || !b) return false
      if (a.fileId && b.fileId) return String(a.fileId) === String(b.fileId)
      return a.fileUrl === b.fileUrl && a.fileName === b.fileName
    },
    isSelected(file) {
      return this.sameFile(file, this.selectedFile)
    },
    isDocument(file) {
      return this.front.normalizePublicFileCategory(file && file.fileCategory) === 'DOCUMENT'
    },
    openFileDetail(file) {
      this.selectedFile = file
      this.clearDownloadError()
      this.previewLoadFailed = false
      this.previewChecking = false
      this.$nextTick(() => this.schedulePreviewProbe())
    },
    onCardClick(file) {
      this.openFileDetail(file)
    },
    closeFileDetail() {
      this.selectedFile = null
      this.clearDownloadError()
      this.previewLoadFailed = false
      this.previewChecking = false
    },
    onPreviewMediaError() {
      this.previewLoadFailed = true
    },
    schedulePreviewProbe() {
      if (!this.selectedFile) return
      // 仅探测本站可直嵌的 PDF/文档流；第三方预览页无法可靠 CORS 探测
      if (!this.isDocumentPreview || !this.canEmbedPreview) return
      const url = this.embedPreviewUrl
      if (!url || /docs\.google\.com|officeapps\.live\.com/i.test(url)) return
      if (/^https?:\/\//i.test(url) && !this.front.isBrowserSameOriginUrl(url)) return
      this.probeDocumentPreview(url)
    },
    async probeDocumentPreview(url) {
      const target = url || this.embedPreviewUrl
      if (!target || !this.selectedFile) return
      const seq = (this._previewProbeSeq = (this._previewProbeSeq || 0) + 1)
      this.previewChecking = true
      this.previewLoadFailed = false
      try {
        const token = localStorage.getItem('paleo_user_token') || ''
        const response = await fetch(target, {
          method: 'GET',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            Range: 'bytes=0-0'
          }
        })
        if (seq !== this._previewProbeSeq) return
        if (!response.ok) {
          this.previewLoadFailed = true
          return
        }
        const ct = (response.headers.get('content-type') || '').toLowerCase()
        // 后端 404/错误页常以 text/html 返回；真实 PDF 应为 application/pdf
        if (ct.includes('text/html') || ct.includes('application/json')) {
          this.previewLoadFailed = true
        }
      } catch (e) {
        if (seq !== this._previewProbeSeq) return
        this.previewLoadFailed = true
      } finally {
        if (seq === this._previewProbeSeq) {
          this.previewChecking = false
        }
      }
    },
    async downloadFile(file) {
      if (!file || this.downloadingKey) return
      this.clearDownloadError()
      this.downloadingKey = this.fileKey(file)
      try {
        await this.front.downloadPublicFile(file)
        if (this.front.selfServiceStatus && /失败|无法|丢失|系统出现问题|请稍后再试/.test(this.front.selfServiceStatus)) {
          this.showDownloadError(this.front.selfServiceStatus)
        }
      } catch (error) {
        this.showDownloadError(resolveDownloadErrorMessage(error))
      } finally {
        this.downloadingKey = ''
      }
    }
  }
}
</script>
